import React, { useState, useEffect } from "react";

import { tr } from "../../i18n";
const PayrollPreviewModal = ({ show, onClose, onConfirm, template }) => {
  const getDefaultSampleData = () => {
    const workingDays = 22;
    const expectedHoursPerDay = 8;
    const attendanceDays = workingDays; // Full attendance
    const attendanceHours = workingDays * expectedHoursPerDay; // Full hours

    return {
      employee_salary: 5000000,
      working_days: workingDays,
      total_attendance_days: attendanceDays,
      total_attendance_hours: attendanceHours,
      total_regular_overtime_hours: 0,
      total_holiday_overtime_hours: 0,
      remaining_paid_leave: 12,
      expected_hours_per_day: expectedHoursPerDay,
      total_late_minutes: 0,
      total_paid_leave_days: 0,
      total_lwop_days: 0,
      total_special_leave_days: 0,
    };
  };

  const [sampleData, setSampleData] = useState(getDefaultSampleData());

  console.log(template);

  const [calculationResult, setCalculationResult] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setHasCalculated(false);
      setCalculationResult(null);
      setSampleData(getDefaultSampleData());
    }
  }, [show]);

  // Calculation Engine
  const evaluateFormula = (formula, variables) => {
    try {
      // Replace variables with value
      let expression = formula;

      Object.keys(variables).forEach((key) => {
        const regex = new RegExp(key, "g");
        expression = expression.replace(regex, variables[key]);
      });

      // Handle percentage
      expression = expression.replace(/(\d+\.?\d*)\s*%/g, "($1 / 100)");

      // Evaluate expression
      // eslint-disable-next-line no-eval
      const result = eval(expression);
      return isNaN(result) ? 0 : result;
    } catch (error) {
      console.error("Formula evaluation error:", error);
      return 0;
    }
  };

  // Helper: Calculate attendance pay based on settings
  const calculateAttendancePay = (
    baseSalary,
    workingDays,
    attendanceDays,
    totalAttendanceHours,
    effectiveHours,
    expectedHoursPerDay,
    totalLateMinutes,
    toleranceMinutes,
    settings,
  ) => {
    // Calculate expected total hours
    const expectedTotalHours = workingDays * expectedHoursPerDay;
    const hourlyRate = baseSalary / expectedTotalHours;

    if (settings.attendance_type === "strict") {
      // Tolerance applies PER DAY, not total monthly
      const excessLateMinutes = Math.max(
        0,
        totalLateMinutes - toleranceMinutes,
      );

      // Convert late minutes to hours
      const lateHoursPenalty = excessLateMinutes / 60;

      // Calculate effective hours after penalty
      const finalEffectiveHours = Math.max(
        0,
        effectiveHours - lateHoursPenalty,
      );

      // Calculate salary based on effective hours
      return hourlyRate * finalEffectiveHours;
    } else {
      // ===== FLEXIBLE MODE =====
      // 1. Free check-in/out time
      // 2. What matters is total working hours achieved

      // Required minimum hours (e.g., 80% of expected)
      const minRequiredHours = expectedTotalHours * 0.8;

      if (effectiveHours >= expectedTotalHours) {
        // Working hours meet or exceed expected -> Full salary
        return baseSalary;
      } else if (effectiveHours >= minRequiredHours) {
        // Working hours between 80%-100% -> Still full salary (flexible!)
        return baseSalary;
      } else {
        // Working hours below 80% -> Prorate deduction
        return hourlyRate * effectiveHours;
      }
    }
  };

  // Helper: Calculate effective hours (after break time)
  const calculateEffectiveHours = (
    totalHours,
    settings,
    isOvertime = false,
  ) => {
    const breakHours = settings.break_time_hours || 0;

    if (settings.break_time_apply_to === "all") {
      // Apply to both attendance and overtime
      return Math.max(0, totalHours - breakHours);
    } else if (
      settings.break_time_apply_to === "attendance_only" &&
      !isOvertime
    ) {
      // Apply only to attendance hours
      return Math.max(0, totalHours - breakHours);
    }

    return totalHours;
  };

  // Helper: Calculate overtime pay and return steps
  const calculateOvertimePay = (
    overtimeHours,
    hourlyRate,
    schema,
    rules = [],
    flatMultiplier = null,
  ) => {
    let steps = []; // Array to store calculation steps

    // ========== FLAT SCHEMA ==========
    if (schema === "flat") {
      const pay = overtimeHours * hourlyRate * flatMultiplier;
      steps.push({
        hours: overtimeHours,
        multiplier: flatMultiplier,
        pay: pay,
        rule_detail: "Flat Multiplier Applied",
        hourlyRate: hourlyRate, // <--- DITAMBAHKAN
      });
      return { total: pay, steps: steps };
    }

    // ========== FIXED SEQUENCE SCHEMA ==========
    if (schema === "fixed_sequence") {
      let total = 0;
      let remaining = overtimeHours;
      let currentHourCount = 0; // Track the current hour being processed (1st, 2nd, 3rd, etc.)

      for (const rule of rules) {
        if (remaining <= 0) break;

        let hoursInRule = 0;
        let ruleDetail = "";

        // SINGLE per jam (hour 1, hour 2)
        if (rule.type === "single" || rule.hour) {
          hoursInRule = 1;
          // Determine the specific hour this rule applies to
          currentHourCount = rule.hour || rules.indexOf(rule) + 1;
          ruleDetail = `Hour ${currentHourCount} (Rule: ${rule.multiplier}x)`;
        }

        // RANGE (from 3 to 7)
        else if (rule.type === "range") {
          // The current hour is the next hour after the last single rule processed, or 'from' hour
          const startHour = rule.from || currentHourCount + 1;
          const endHour = rule.to || Infinity;

          // Hours available in this range rule, starting from startHour
          const hoursAvailableInRule = endHour - startHour + 1;

          // Hours to be processed: minimum of remaining hours, or hours available in this rule
          hoursInRule = Math.min(remaining, hoursAvailableInRule);

          currentHourCount = startHour + hoursInRule - 1; // Update current hour count to the end of this range
          ruleDetail = `Hours ${startHour} to ${
            startHour + hoursInRule - 1
          } (Rule: ${rule.multiplier}x)`;
        }

        // Only calculate if hoursInRule is positive (i.e., we haven't exhausted the rule or remaining hours)
        if (hoursInRule > 0) {
          const pay = hoursInRule * hourlyRate * rule.multiplier;
          total += pay;
          remaining -= hoursInRule;

          steps.push({
            hours: hoursInRule,
            multiplier: rule.multiplier,
            pay: pay,
            rule_detail: ruleDetail,
            hourlyRate: hourlyRate, // <--- DITAMBAHKAN
          });
        }
      }

      // JAM SISA → ambil multiplier terakhir
      if (remaining > 0 && rules.length > 0) {
        const last = rules[rules.length - 1];
        const lastMultiplier = last.multiplier;
        const pay = remaining * hourlyRate * lastMultiplier;
        total += pay;

        // Calculate the starting hour for remaining hours
        const startHour = currentHourCount + 1;

        steps.push({
          hours: remaining,
          multiplier: lastMultiplier,
          pay: pay,
          rule_detail: `Hours ${startHour} onwards (used last rule multiplier: ${lastMultiplier}x)`,
          hourlyRate: hourlyRate, // <--- DITAMBAHKAN
        });
      }

      return { total: total, steps: steps };
    }

    // ========== INCREASING SCHEMA ==========
    // rules: [{after_hours: 1, multiplier: 1.5}, {after_hours: 3, multiplier: 2}]
    if (schema === "increasing") {
      const sortedRules = [...rules].sort(
        (a, b) => a.after_hours - b.after_hours,
      );

      let totalPay = 0;
      let remainingHours = overtimeHours;
      let previousThreshold = 0;
      let startHourForStep = 1;

      for (const rule of sortedRules) {
        if (remainingHours <= 0) break;

        const hoursInBracket = Math.min(
          remainingHours,
          rule.after_hours - previousThreshold,
        );

        if (hoursInBracket > 0) {
          const pay = hoursInBracket * hourlyRate * rule.multiplier;
          totalPay += pay;
          remainingHours -= hoursInBracket;
          previousThreshold = rule.after_hours;

          const endHourForStep = startHourForStep + hoursInBracket - 1;

          steps.push({
            hours: hoursInBracket,
            multiplier: rule.multiplier,
            pay: pay,
            rule_detail: `Hours ${startHourForStep} to ${endHourForStep} (Rule: ${rule.after_hours}h threshold)`,
            hourlyRate: hourlyRate, // <--- DITAMBAHKAN
          });
          startHourForStep = endHourForStep + 1;
        }
      }

      // JAM SISANYA → multiplier terakhir
      if (remainingHours > 0 && sortedRules.length > 0) {
        const lastMultiplier = sortedRules[sortedRules.length - 1].multiplier;
        const pay = remainingHours * hourlyRate * lastMultiplier;
        totalPay += pay;

        steps.push({
          hours: remainingHours,
          multiplier: lastMultiplier,
          pay: pay,
          rule_detail: `Hours ${startHourForStep} onwards (used last rule multiplier: ${lastMultiplier}x)`,
          hourlyRate: hourlyRate, // <--- DITAMBAHKAN
        });
      }
      return { total: totalPay, steps: steps };
    }

    // Default return
    return { total: 0, steps: [] };
  };

  const calculatePayroll = () => {
    if (!template || !template.payroll_groups || !template.payroll_items) {
      alert(tr("payroll:invalid_template_data"));
      return;
    }

    setIsCalculating(true);

    // Reset previous result (biar bisa re-calculate)
    setCalculationResult(null);
    setHasCalculated(false);

    const settings = template.settings || {};

    // ===== PROCESS SETTINGS FIRST =====

    // 1. Calculate effective hours FIRST (before attendance pay)
    const effectiveAttendanceHours =
      settings.attendance_type === "strict"
        ? sampleData.total_attendance_hours // STRICT TIDAK BOLEH DIPOTONG BREAK
        : calculateEffectiveHours(
            sampleData.total_attendance_hours,
            settings,
            false,
          );

    // 2. Calculate attendance pay based on EFFECTIVE HOURS
    const calculatedAttendancePay = calculateAttendancePay(
      sampleData.employee_salary,
      sampleData.working_days,
      sampleData.total_attendance_days,
      sampleData.total_attendance_hours,
      effectiveAttendanceHours,
      sampleData.expected_hours_per_day,
      sampleData.total_late_minutes,
      settings.tolerance_minutes || 0,
      settings,
    );

    // 3. Calculate hourly rate for overtime
    // Must use expected_hours_per_day to match backend (not hardcoded 8)
    const hourlyRate =
      sampleData.employee_salary /
      (sampleData.working_days * sampleData.expected_hours_per_day);

    // 4. Calculate regular overtime pay
    const regularOvertimeHours = calculateEffectiveHours(
      sampleData.total_regular_overtime_hours,
      settings,
      true, // is overtime
    );
    let regularOvertimeResult = { total: 0, steps: [] }; // New structure
    let regularOvertimeRules = [];
    let regularOvertimeRate = "";

    // choose rules based on schema
    if (settings.overtime_regular_schema === "flat") {
      // FLAT MODE
      regularOvertimeResult = calculateOvertimePay(
        regularOvertimeHours,
        hourlyRate,
        "flat",
        [],
        settings.regular_flat_multiplier,
      );
      regularOvertimeRate = `${settings.regular_flat_multiplier}x`;
    } else if (settings.overtime_regular_schema === "fixed_sequence") {
      // FIXED SEQUENCE MODE
      regularOvertimeRules = settings.overtime_regular_fixed_sequence || [];
      regularOvertimeResult = calculateOvertimePay(
        regularOvertimeHours,
        hourlyRate,
        "fixed_sequence", // <── correct schema
        regularOvertimeRules, // <── regular rules
        settings.regular_flat_multiplier, // <── flat value (fallback)
      );
      // For fixed_sequence, rate is not single, leave as 'Varies'
      regularOvertimeRate = "Varies";
    } else if (settings.overtime_regular_schema === "increasing") {
      // INCREASING MODE
      regularOvertimeRules = settings.overtime_increase_rules || [];
      regularOvertimeResult = calculateOvertimePay(
        regularOvertimeHours,
        hourlyRate,
        "increasing", // <── correct schema
        regularOvertimeRules, // <── increasing rules
        settings.regular_flat_multiplier,
      );
      regularOvertimeRate = "Varies";
    }
    let regularOvertimePay = regularOvertimeResult.total; // Get total pay

    // 5. Calculate holiday overtime pay
    const holidayOvertimeHours = calculateEffectiveHours(
      sampleData.total_holiday_overtime_hours,
      settings,
      true, // is overtime
    );
    let holidayOvertimeResult = { total: 0, steps: [] }; // New structure
    let holidayOvertimeRules = [];
    let holidayOvertimeRate = "";

    // Holiday overtime schema
    if (settings.overtime_holiday_schema === "flat") {
      holidayOvertimeResult = calculateOvertimePay(
        holidayOvertimeHours,
        hourlyRate,
        "flat",
        [],
        settings.holiday_flat_multiplier,
      );
      holidayOvertimeRate = `${settings.holiday_flat_multiplier}x`;
    } else if (settings.overtime_holiday_schema === "fixed_sequence") {
      holidayOvertimeRules = settings.overtime_holiday_fixed_sequence || [];
      holidayOvertimeResult = calculateOvertimePay(
        holidayOvertimeHours,
        hourlyRate,
        "fixed_sequence",
        holidayOvertimeRules,
        settings.holiday_flat_multiplier,
      );
      holidayOvertimeRate = "Varies";
    } else if (settings.overtime_holiday_schema === "increasing") {
      holidayOvertimeRules = settings.overtime_increase_rules || [];
      holidayOvertimeResult = calculateOvertimePay(
        holidayOvertimeHours,
        hourlyRate,
        "increasing",
        holidayOvertimeRules,
        settings.holiday_flat_multiplier,
      );
      holidayOvertimeRate = "Varies";
    }
    let holidayOvertimePay = holidayOvertimeResult.total; // Get total pay

    // ===== PREPARE VARIABLES — must match PayrollCalculationController.php
    const totalPaidLeaveDays = sampleData.total_paid_leave_days || 0;
    const totalLwopDays = sampleData.total_lwop_days || 0;
    const totalSpecialLeaveDays = sampleData.total_special_leave_days || 0;
    const expectedTotalHours =
      sampleData.working_days * sampleData.expected_hours_per_day;
    const absentDays = Math.max(
      0,
      sampleData.working_days -
        sampleData.total_attendance_days -
        totalLwopDays -
        totalPaidLeaveDays -
        totalSpecialLeaveDays,
    );
    const attendancePct =
      sampleData.working_days > 0
        ? Math.round(
            (sampleData.total_attendance_days / sampleData.working_days) *
              10000,
          ) / 100
        : 0;
    const hoursCompletionPct =
      expectedTotalHours > 0
        ? Math.round((effectiveAttendanceHours / expectedTotalHours) * 10000) /
          100
        : 0;
    const lateDeductionHours = Math.max(
      0,
      Math.round(
        ((sampleData.total_late_minutes - (settings.tolerance_minutes || 0)) /
          60) *
          100,
      ) / 100,
    );

    const variables = {
      attendance_pay: calculatedAttendancePay,
      employee_salary: sampleData.employee_salary,
      salary_monthly_rate: sampleData.employee_salary,
      salary_hourly_rate: hourlyRate,
      salary_daily_rate: sampleData.employee_salary / sampleData.working_days,
      working_days: sampleData.working_days,
      expected_hours_per_day: sampleData.expected_hours_per_day,
      expected_total_hours: expectedTotalHours,
      total_attendance_days: sampleData.total_attendance_days,
      total_attendance_hours: effectiveAttendanceHours,
      total_attendance_hours_raw: sampleData.total_attendance_hours,
      absent_days: absentDays,
      attendance_percentage: attendancePct,
      hours_completion_percentage: hoursCompletionPct,
      total_leave_days:
        totalPaidLeaveDays + totalLwopDays + totalSpecialLeaveDays,
      total_paid_leave_days: totalPaidLeaveDays,
      total_lwop_days: totalLwopDays,
      total_special_leave_days: totalSpecialLeaveDays,
      remaining_paid_leave: sampleData.remaining_paid_leave,
      total_regular_overtime_hours: regularOvertimeHours,
      total_regular_overtime_hours_raw: sampleData.total_regular_overtime_hours,
      total_holiday_overtime_hours: holidayOvertimeHours,
      total_holiday_overtime_hours_raw: sampleData.total_holiday_overtime_hours,
      total_overtime_hours: regularOvertimeHours + holidayOvertimeHours,
      regular_overtime_pay: regularOvertimePay,
      holiday_overtime_pay: holidayOvertimePay,
      total_overtime_pay: regularOvertimePay + holidayOvertimePay,
      taxable_income: 0,
      late_hours: sampleData.total_late_minutes / 60,
      late_minutes: sampleData.total_late_minutes,
      late_days: Math.ceil(
        sampleData.total_late_minutes /
          (sampleData.expected_hours_per_day * 60),
      ),
      late_deduction_hours: lateDeductionHours,
    };

    const groupResults = [];
    let runningTotal = 0;
    let taxableIncomeTotal = 0;

    // First pass: Calculate taxable income
    template.payroll_items.forEach((item) => {
      if (item.is_taxable && item.type === "income") {
        const value = evaluateFormula(item.formula, variables);
        taxableIncomeTotal += value;
      }
    });

    variables.taxable_income = taxableIncomeTotal;

    // Calculate each group
    template.payroll_groups.forEach((group) => {
      const groupItems = template.payroll_items.filter(
        (item) => item.group_id === group.id,
      );

      if (groupItems.length === 0) return;

      let groupSubtotal = 0; // Tambahkan inisialisasi ini

      const itemResults = groupItems.map((item) => {
        const value = evaluateFormula(item.formula, variables);

        // LOGIKA BARU: Item yang menentukan +/- ke runningTotal
        if (item.operator === "subtract") {
          runningTotal -= value;
          groupSubtotal -= value;
        } else {
          runningTotal += value;
          groupSubtotal += value;
        }

        return {
          ...item,
          calculated_value: value,
        };
      });

      // HAPUS bagian if (group.operator === "add") {...} yang lama

      groupResults.push({
        group,
        items: itemResults,
        subtotal: groupSubtotal,
      });
    });

    setCalculationResult({
      groups: groupResults,
      settings_applied: {
        attendance_type: settings.attendance_type,
        calculated_attendance_pay: calculatedAttendancePay,
        break_time_deduction: {
          attendance_hours:
            sampleData.total_attendance_hours - effectiveAttendanceHours,
          regular_overtime:
            sampleData.total_regular_overtime_hours - regularOvertimeHours,
          holiday_overtime:
            sampleData.total_holiday_overtime_hours - holidayOvertimeHours,
        },
        overtime_calculation: {
          regular: {
            hours: regularOvertimeHours,
            schema: settings.overtime_regular_schema,
            rules: regularOvertimeRules,
            rate: regularOvertimeRate,
            pay: regularOvertimePay,
            calculation_steps: regularOvertimeResult.steps, // <--- DITAMBAHKAN
          },
          holiday: {
            hours: holidayOvertimeHours,
            schema: settings.overtime_holiday_schema,
            rules: holidayOvertimeRules,
            rate: holidayOvertimeRate,
            pay: holidayOvertimePay,
            calculation_steps: holidayOvertimeResult.steps, // <--- DITAMBAHKAN
          },
        },
      },
      taxable_income: taxableIncomeTotal,
      gross_salary: groupResults
        .filter((g) => g.group.operator === "add")
        .reduce((sum, g) => sum + g.subtotal, 0),
      total_deductions: Math.abs(
        groupResults
          .filter((g) => g.group.operator === "subtract")
          .reduce((sum, g) => sum + g.subtotal, 0),
      ),
      net_salary: runningTotal,
    });

    setHasCalculated(true);
    setIsCalculating(false);
  };

  const handleConfirm = () => {
    if (!hasCalculated) {
      alert(tr("payroll:please_calculate_the_preview_first_before"));
      return;
    }
    onConfirm();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  if (!show) return null;

  return (
    <>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
        rel="stylesheet"
      />

      <div
        className="modal show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-scrollable"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">
                <i className="fas fa-eye me-2"></i>
                {tr("payroll:payroll_template_preview")}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body text-start">
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-header bg-white border-bottom py-3 text-start">
                  <h5 className="mb-0 text-primary fw-bold text-start">
                    <i className="fas fa-sliders-h me-2"></i>{tr("payroll:sample_input_data")}
                  </h5>
                  <small className="text-muted text-start">
                    {tr("payroll:enter_simulation_data_below_to_preview")}
                  </small>
                </div>

                <div className="card-body">
                  <div className="row g-4 mb-4">
                    {/* Salary */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:employee_salary_2")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-wallet text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.employee_salary}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              employee_salary: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">{tr("payroll:example_5_000_000")}</small>
                    </div>

                    {/* Working Days */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("shared:working_days")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-calendar-check text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.working_days}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              working_days: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:total_working_days_per_month")}
                      </small>
                    </div>

                    {/* Attendance Days */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:attendance_days_2")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-calendar-day text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_attendance_days}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_attendance_days:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:employee_attendance_days")}
                      </small>
                    </div>

                    {/* Attendance Hours */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:attendance_hours")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-clock text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_attendance_hours}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_attendance_hours:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:total_hours_attended_per_month")}
                      </small>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:expected_hours_per_day")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-business-time text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.expected_hours_per_day}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              expected_hours_per_day:
                                parseFloat(e.target.value) || 8,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:required_working_hours_per_day")}
                      </small>
                    </div>

                    {/* Conditional: Only show if template.settings.attendance_type === 'strict' */}
                    {template?.settings?.attendance_type === "strict" && (
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-start d-block">
                          {tr("payroll:total_late_minutes_strict_mode")}
                        </label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light">
                            <i className="fas fa-exclamation-triangle text-warning"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={sampleData.total_late_minutes}
                            onChange={(e) =>
                              setSampleData({
                                ...sampleData,
                                total_late_minutes:
                                  parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                        <small className="text-muted">
                          {tr("payroll:total_late_minutes_this_month_tolerance")}{" "}
                          {template.settings.tolerance_minutes} {" "}{tr("payroll:min_day")}
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="row g-4">
                    {/* Regular OT */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:regular_overtime_hours")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-hourglass-half text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_regular_overtime_hours}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_regular_overtime_hours:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Holiday OT */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:holiday_overtime_hours")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-sun text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_holiday_overtime_hours}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_holiday_overtime_hours:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Remaining Paid Leave */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:remaining_paid_leave_balance")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-plane-departure text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.remaining_paid_leave}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              remaining_paid_leave:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:remaining_quota_not_days_taken")}
                      </small>
                    </div>

                    {/* Paid Leave Days Taken */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:paid_leave_days_taken")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-calendar-minus text-success"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_paid_leave_days}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_paid_leave_days:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:used_for")}{" "} <code>total_leave_days</code> &amp;{" "}
                        <code>absent_days</code>
                      </small>
                    </div>

                    {/* LWOP Days */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:leave_without_pay_lwop_days")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-calendar-times text-danger"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_lwop_days}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_lwop_days: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:unpaid_leave_days_this_period")}
                      </small>
                    </div>

                    {/* Special Leave Days */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-start d-block">
                        {tr("payroll:special_leave_sl_days")}
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light">
                          <i className="fas fa-star text-primary"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={sampleData.total_special_leave_days}
                          onChange={(e) =>
                            setSampleData({
                              ...sampleData,
                              total_special_leave_days:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <small className="text-muted">
                        {tr("payroll:paid_leave_days_that_do_not")}
                      </small>
                    </div>
                  </div>

                  {/* Calculate Button */}
                  <div className="text-center mt-4">
                    <button
                      className="btn btn-primary btn-lg px-5 shadow-sm"
                      onClick={calculatePayroll}
                      disabled={isCalculating}
                    >
                      <i
                        className={`fas ${
                          isCalculating ? "fa-spinner fa-spin" : "fa-calculator"
                        } me-2`}
                      ></i>
                      {isCalculating
                        ? tr("payroll:calculating")
                        : hasCalculated
                          ? tr("payroll:re_calculate_payroll")
                          : tr("payroll:calculate_payroll_2")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Calculation Result Section */}
              {calculationResult && (
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-receipt me-2"></i>
                      {tr("payroll:calculation_result")}
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Taxable Income Info */}
                    <div className="alert alert-warning mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>{tr("payroll:taxable_income_2")}</strong>{" "}
                      {formatCurrency(calculationResult.taxable_income)}
                    </div>

                    {/* Settings Applied Info */}
                    <div className="card mb-3 border-info">
                      <div className="card-header bg-info text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-cog me-2"></i>
                          {tr("payroll:settings_applied_calculation_details")}
                        </h6>
                      </div>
                      <div className="card-body">
                        {/* ===== ATTENDANCE CALCULATION ===== */}
                        <div className="mb-4">
                          <h6 className="text-primary border-bottom pb-2">
                            <i className="fas fa-user-check me-2"></i>
                            {tr("payroll:attendance_calculation")}
                          </h6>
                          <div className="row">
                            <div className="col-md-6">
                              <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:attendance_type")}</strong>
                                    </td>
                                    <td>
                                      <span
                                        className={`badge bg-${
                                          calculationResult.settings_applied
                                            .attendance_type === "strict"
                                            ? "danger"
                                            : "success"
                                        }`}
                                      >
                                        {calculationResult.settings_applied.attendance_type.toUpperCase()}
                                      </span>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:expected_hours_day")}</strong>
                                    </td>
                                    <td>
                                      {sampleData.expected_hours_per_day} {" "}{tr("payroll:hours")}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:expected_total_hours")}</strong>
                                    </td>
                                    <td>
                                      {sampleData.expected_hours_per_day *
                                        sampleData.working_days}{" "}
                                      {tr("payroll:hours")}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:actual_attendance_hours")}</strong>
                                    </td>
                                    <td>
                                      {sampleData.total_attendance_hours} {" "}{tr("payroll:hours")}
                                    </td>
                                  </tr>
                                  {calculationResult.settings_applied
                                    .break_time_deduction.attendance_hours >
                                    0 && (
                                    <tr>
                                      <td>
                                        <strong className="text-danger">
                                          {tr("payroll:break_time_deducted")}
                                        </strong>
                                      </td>
                                      <td className="text-danger">
                                        -{" "}
                                        {
                                          calculationResult.settings_applied
                                            .break_time_deduction
                                            .attendance_hours
                                        }{" "}
                                        {tr("payroll:hours")}
                                      </td>
                                    </tr>
                                  )}
                                  <tr className="table-active">
                                    <td>
                                      <strong>{tr("payroll:effective_hours")}</strong>
                                    </td>
                                    <td>
                                      <strong>
                                        {sampleData.total_attendance_hours -
                                          calculationResult.settings_applied
                                            .break_time_deduction
                                            .attendance_hours}{" "}
                                        {tr("payroll:hours")}
                                      </strong>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            <div className="col-md-6">
                              <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:employee_salary")}</strong>
                                    </td>
                                    <td>
                                      {formatCurrency(
                                        sampleData.employee_salary,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:hourly_rate")}</strong>
                                    </td>
                                    <td>
                                      {formatCurrency(
                                        sampleData.employee_salary /
                                          (sampleData.working_days *
                                            sampleData.expected_hours_per_day),
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>
                                      <strong>{tr("payroll:daily_rate")}</strong>
                                    </td>
                                    <td>
                                      {formatCurrency(
                                        sampleData.employee_salary /
                                          sampleData.working_days,
                                      )}
                                    </td>
                                  </tr>

                                  {/* STRICT MODE INFO */}
                                  {calculationResult.settings_applied
                                    .attendance_type === "strict" && (
                                    <>
                                      {sampleData.total_late_minutes > 0 && (
                                        <>
                                          <tr>
                                            <td>
                                              <strong className="text-warning">
                                                {tr("payroll:total_late")}
                                              </strong>
                                            </td>
                                            <td className="text-warning">
                                              {sampleData.total_late_minutes}{" "}
                                              {tr("payroll:minutes_2")}
                                              {(
                                                sampleData.total_late_minutes /
                                                60
                                              ).toFixed(2)}{" "}
                                              {tr("payroll:hours_2")}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong>{tr("payroll:tolerance_day")}</strong>
                                            </td>
                                            <td>
                                              {template.settings
                                                .tolerance_minutes || 0}{" "}
                                              {tr("payroll:minutes")}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td>
                                              <strong className="text-danger">
                                                {tr("payroll:excess_late")}
                                              </strong>
                                            </td>
                                            <td className="text-danger">
                                              {Math.max(
                                                0,
                                                sampleData.total_late_minutes -
                                                  (template.settings
                                                    .tolerance_minutes || 0),
                                              )}{" "}
                                              {tr("payroll:minutes_deducted")}
                                            </td>
                                          </tr>
                                        </>
                                      )}
                                    </>
                                  )}

                                  {/* FLEXIBLE MODE INFO */}
                                  {calculationResult.settings_applied
                                    .attendance_type === "flexible" && (
                                    <>
                                      <tr>
                                        <td>
                                          <strong>{tr("payroll:min_required_80")}</strong>
                                        </td>
                                        <td>
                                          {(
                                            sampleData.expected_hours_per_day *
                                            sampleData.working_days *
                                            0.8
                                          ).toFixed(0)}{" "}
                                          {tr("payroll:hours")}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>{tr("payroll:status")}</strong>
                                        </td>
                                        <td>
                                          {sampleData.total_attendance_hours -
                                            calculationResult.settings_applied
                                              .break_time_deduction
                                              .attendance_hours >=
                                          sampleData.expected_hours_per_day *
                                            sampleData.working_days *
                                            0.8 ? (
                                            <span className="badge bg-success">
                                              {tr("payroll:qualified_for_full_salary")}
                                            </span>
                                          ) : (
                                            <span className="badge bg-warning">
                                              {tr("payroll:below_minimum_prorated")}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    </>
                                  )}

                                  <tr className="table-success">
                                    <td>
                                      <strong>{tr("payroll:final_attendance_pay")}</strong>
                                    </td>
                                    <td>
                                      <strong className="text-success">
                                        {formatCurrency(
                                          calculationResult.settings_applied
                                            .calculated_attendance_pay,
                                        )}
                                      </strong>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* ===== OVERTIME CALCULATION ===== */}
                        {(sampleData.total_regular_overtime_hours > 0 ||
                          sampleData.total_holiday_overtime_hours > 0) && (
                          <div className="mb-4">
                            <h6 className="text-primary border-bottom pb-2">
                              <i className="fas fa-clock me-2"></i>
                              {tr("payroll:overtime_calculation")}
                            </h6>

                            {/* REGULAR OVERTIME */}
                            {sampleData.total_regular_overtime_hours > 0 && (
                              <div className="mb-3">
                                <strong className="text-secondary">
                                  {tr("payroll:regular_overtime")}
                                </strong>
                                <table className="table table-sm table-borderless mt-2">
                                  <tbody>
                                    <tr>
                                      <td width="200">
                                        <strong>{tr("payroll:input_hours")}</strong>
                                      </td>
                                      <td>
                                        {
                                          sampleData.total_regular_overtime_hours
                                        }{" "}
                                        {tr("payroll:hours")}
                                      </td>
                                    </tr>
                                    {calculationResult.settings_applied
                                      .break_time_deduction.regular_overtime >
                                      0 && (
                                      <tr>
                                        <td>
                                          <strong className="text-danger">
                                            {tr("payroll:break_deducted")}
                                          </strong>
                                        </td>
                                        <td className="text-danger">
                                          -{" "}
                                          {
                                            calculationResult.settings_applied
                                              .break_time_deduction
                                              .regular_overtime
                                          }{" "}
                                          {tr("payroll:hours")}
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td>
                                        <strong>{tr("payroll:effective_hours")}</strong>
                                      </td>
                                      <td>
                                        <strong>
                                          {
                                            calculationResult.settings_applied
                                              .overtime_calculation.regular
                                              .hours
                                          }{" "}
                                          {tr("payroll:hours")}
                                        </strong>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>
                                        <strong>{tr("shared:schema")}</strong>
                                      </td>
                                      <td>
                                        <span className="badge bg-info">
                                          {
                                            calculationResult.settings_applied
                                              .overtime_calculation.regular
                                              .schema
                                          }
                                        </span>
                                      </td>
                                    </tr>
                                    {calculationResult.settings_applied
                                      .overtime_calculation.regular.rules
                                      .length > 0 && (
                                      <tr>
                                        <td>
                                          <strong>{tr("payroll:rules_applied")}</strong>
                                        </td>
                                        <td>
                                          <ul className="mb-0 ps-3">
                                            {calculationResult.settings_applied.overtime_calculation.regular.rules.map(
                                              (rule, i) => (
                                                <li key={i} className="small">
                                                  {/* Display Hour for fixed_sequence */}
                                                  {rule.type === "single" ||
                                                  rule.hour
                                                    ? `Hour ${
                                                        rule.hour || i + 1
                                                      }`
                                                    : `Range ${rule.from}-${rule.to}`}
                                                  :{" "}
                                                  <strong>
                                                    {rule.multiplier}x
                                                  </strong>
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </td>
                                      </tr>
                                    )}

                                    {/* --- Calculation Breakdown Added Here --- */}
                                    {calculationResult.settings_applied
                                      .overtime_calculation.regular
                                      .calculation_steps.length > 0 && (
                                      <tr>
                                        <td colSpan="2">
                                          <strong className="text-dark d-block mt-3 mb-1">
                                            {tr("payroll:calculation_breakdown")}
                                          </strong>
                                          <table>
                                            <thead>
                                              <tr className="table-secondary">
                                                <th>{tr("payroll:hours_applied")}</th>
                                                <th>{tr("payroll:multiplier")}</th>
                                                <th>{tr("payroll:calculation")}</th>
                                                <th className="text-end">
                                                  {tr("payroll:pay")}
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {calculationResult.settings_applied.overtime_calculation.regular.calculation_steps.map(
                                                (step, i) => (
                                                  <tr key={i}>
                                                    <td>
                                                      {step.hours}h (
                                                      {step.rule_detail})
                                                    </td>
                                                    <td>{step.multiplier}x</td>
                                                    <td>
                                                      <code
                                                        style={{
                                                          fontSize: "11px",
                                                          whiteSpace: "nowrap",
                                                        }}
                                                      >
                                                        {step.hours} *{" "}
                                                        {formatCurrency(
                                                          step.hourlyRate,
                                                        )}{" "}
                                                        * {step.multiplier}
                                                      </code>
                                                    </td>
                                                    <td className="text-end">
                                                      {formatCurrency(step.pay)}
                                                    </td>
                                                  </tr>
                                                ),
                                              )}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                    {/* ------------------------------------------- */}

                                    <tr className="table-light">
                                      <td>
                                        <strong>{tr("payroll:regular_ot_pay")}</strong>
                                      </td>
                                      <td>
                                        <strong className="text-success">
                                          {formatCurrency(
                                            calculationResult.settings_applied
                                              .overtime_calculation.regular.pay,
                                          )}
                                        </strong>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* HOLIDAY OVERTIME */}
                            {sampleData.total_holiday_overtime_hours > 0 && (
                              <div>
                                <strong className="text-warning">
                                  {tr("payroll:holiday_overtime_2")}
                                </strong>
                                <table className="table table-sm table-borderless mt-2">
                                  <tbody>
                                    <tr>
                                      <td width="200">
                                        <strong>{tr("payroll:input_hours")}</strong>
                                      </td>
                                      <td>
                                        {
                                          sampleData.total_holiday_overtime_hours
                                        }{" "}
                                        {tr("payroll:hours")}
                                      </td>
                                    </tr>
                                    {calculationResult.settings_applied
                                      .break_time_deduction.holiday_overtime >
                                      0 && (
                                      <tr>
                                        <td>
                                          <strong className="text-danger">
                                            {tr("payroll:break_deducted")}
                                          </strong>
                                        </td>
                                        <td className="text-danger">
                                          -{" "}
                                          {
                                            calculationResult.settings_applied
                                              .break_time_deduction
                                              .holiday_overtime
                                          }{" "}
                                          {tr("payroll:hours")}
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td>
                                        <strong>{tr("payroll:effective_hours")}</strong>
                                      </td>
                                      <td>
                                        <strong>
                                          {
                                            calculationResult.settings_applied
                                              .overtime_calculation.holiday
                                              .hours
                                          }{" "}
                                          {tr("payroll:hours")}
                                        </strong>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>
                                        <strong>{tr("shared:schema")}</strong>
                                      </td>
                                      <td>
                                        <span className="badge bg-warning text-dark">
                                          {
                                            calculationResult.settings_applied
                                              .overtime_calculation.holiday
                                              .schema
                                          }
                                        </span>
                                      </td>
                                    </tr>
                                    {calculationResult.settings_applied
                                      .overtime_calculation.holiday.rules
                                      .length > 0 && (
                                      <tr>
                                        <td>
                                          <strong>{tr("payroll:rules_applied")}</strong>
                                        </td>
                                        <td>
                                          <ul className="mb-0 ps-3">
                                            {calculationResult.settings_applied.overtime_calculation.holiday.rules.map(
                                              (rule, i) => (
                                                <li key={i} className="small">
                                                  {rule.type === "single" ||
                                                  rule.hour
                                                    ? `Hour ${
                                                        rule.hour || i + 1
                                                      }`
                                                    : `Range ${rule.from}-${rule.to}`}
                                                  :{" "}
                                                  <strong>
                                                    {rule.multiplier}x
                                                  </strong>
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </td>
                                      </tr>
                                    )}

                                    {/* --- Calculation Breakdown Added Here --- */}
                                    {calculationResult.settings_applied
                                      .overtime_calculation.holiday
                                      .calculation_steps.length > 0 && (
                                      <tr>
                                        <td colSpan="2">
                                          <strong className="text-dark d-block mt-3 mb-1">
                                            {tr("payroll:calculation_breakdown")}
                                          </strong>
                                          <table>
                                            <thead>
                                              <tr className="table-secondary">
                                                <th>{tr("payroll:hours_applied")}</th>
                                                <th>{tr("payroll:multiplier")}</th>
                                                <th>{tr("payroll:calculation")}</th>
                                                <th className="text-end">
                                                  {tr("payroll:pay")}
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {calculationResult.settings_applied.overtime_calculation.holiday.calculation_steps.map(
                                                (step, i) => (
                                                  <tr key={i}>
                                                    <td>
                                                      {step.hours}h (
                                                      {step.rule_detail})
                                                    </td>
                                                    <td>{step.multiplier}x</td>
                                                    <td>
                                                      <code
                                                        style={{
                                                          fontSize: "11px",
                                                          whiteSpace: "nowrap",
                                                        }}
                                                      >
                                                        {step.hours} *{" "}
                                                        {formatCurrency(
                                                          step.hourlyRate,
                                                        )}{" "}
                                                        * {step.multiplier}
                                                      </code>
                                                    </td>
                                                    <td className="text-end">
                                                      {formatCurrency(step.pay)}
                                                    </td>
                                                  </tr>
                                                ),
                                              )}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                    {/* ----------------------------------------------------------------------------------------- */}

                                    <tr className="table-light">
                                      <td>
                                        <strong>{tr("payroll:holiday_ot_pay")}</strong>
                                      </td>
                                      <td>
                                        <strong className="text-success">
                                          {formatCurrency(
                                            calculationResult.settings_applied
                                              .overtime_calculation.holiday.pay,
                                          )}
                                        </strong>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ===== SUMMARY (dilewati karena tidak diubah) */}
                        <div className="alert alert-light border mb-0">
                          <div className="row text-center">
                            <div className="col-4">
                              <small className="text-muted d-block">
                                {tr("payroll:base_attendance_pay")}
                              </small>
                              <strong className="text-primary">
                                {formatCurrency(
                                  calculationResult.settings_applied
                                    .calculated_attendance_pay,
                                )}
                              </strong>
                            </div>
                            <div className="col-4">
                              <small className="text-muted d-block">
                                {tr("payroll:total_overtime_pay")}
                              </small>
                              <strong className="text-info">
                                {formatCurrency(
                                  calculationResult.settings_applied
                                    .overtime_calculation.regular.pay +
                                    calculationResult.settings_applied
                                      .overtime_calculation.holiday.pay,
                                )}
                              </strong>
                            </div>
                            <div className="col-4">
                              <small className="text-muted d-block">
                                {tr("payroll:taxable_income")}
                              </small>
                              <strong className="text-warning">
                                {formatCurrency(
                                  calculationResult.taxable_income,
                                )}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Groups Breakdown (dilewati karena tidak diubah) */}
                    {calculationResult.groups.map((groupResult, idx) => (
                      <div key={idx} className="mb-4">
                        <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                          <h6 className="mb-0 text-primary">
                            <i className="fas fa-folder-open me-2"></i>
                            {groupResult.group.name}
                          </h6>
                          {/* Badge operator group sudah dihapus dari sini */}
                        </div>

                        <table className="table table-sm table-bordered">
                          {" "}
                          {/* Tambahkan class table agar rapi */}
                          <thead className="table-light">
                            <tr>
                              <th>{tr("payroll:item_name")}</th>
                              <th>{tr("payroll:formula")}</th>
                              <th className="text-end">{tr("payroll:amount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupResult.items.map((item, itemIdx) => (
                              <tr key={itemIdx}>
                                <td>
                                  <strong>{item.name}</strong>
                                  {item.is_taxable && (
                                    <span
                                      className="badge bg-warning ms-2"
                                      style={{ fontSize: "9px" }}
                                    >
                                      {tr("payroll:taxable")}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <code
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {item.formula}
                                  </code>
                                </td>
                                <td className="text-end">
                                  <span
                                    className={`fw-bold ${
                                      item.operator === "subtract" // Warna teks berdasarkan operator item
                                        ? "text-danger"
                                        : "text-success"
                                    }`}
                                  >
                                    {item.operator === "subtract" ? "- " : ""}
                                    {formatCurrency(item.calculated_value)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                            <tr className="table-active">
                              <td colSpan="2" className="text-end">
                                <strong>
                                  {tr("payroll:subtotal")}{" "} {groupResult.group.name}:
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong
                                  className={
                                    groupResult.subtotal >= 0
                                      ? "text-success"
                                      : "text-danger"
                                  }
                                >
                                  {formatCurrency(groupResult.subtotal)}
                                </strong>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ))}

                    {/* Summary (dilewati karena tidak diubah) */}
                    <div className="card bg-light mt-4">
                      <div className="card-body">
                        <div className="row text-center">
                          <div className="col-md-4">
                            <div className="p-3 border-end">
                              <i className="fas fa-arrow-up text-success fa-2x mb-2"></i>
                              <h6 className="text-muted mb-1">{tr("payroll:gross_salary_2")}</h6>
                              <h4 className="text-success mb-0">
                                {formatCurrency(calculationResult.gross_salary)}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="p-3 border-end">
                              <i className="fas fa-arrow-down text-danger fa-2x mb-2"></i>
                              <h6 className="text-muted mb-1">
                                {tr("payroll:total_deductions")}
                              </h6>
                              <h4 className="text-danger mb-0">
                                -{" "}
                                {formatCurrency(
                                  calculationResult.total_deductions,
                                )}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="p-3">
                              <i className="fas fa-wallet text-primary fa-2x mb-2"></i>
                              <h6 className="text-muted mb-1">{tr("payroll:net_salary")}</h6>
                              <h3 className="text-primary mb-0">
                                {formatCurrency(calculationResult.net_salary)}
                              </h3>
                              <small className="text-muted">
                                {tr("payroll:take_home_pay")}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Success Message */}
                    <div className="alert alert-success mt-3 mb-0">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>{tr("payroll:preview_calculation_completed")}</strong> {" "}{tr("payroll:review_the_results_above_and_click")}
                    </div>
                  </div>
                </div>
              )}

              {/* Info if not calculated yet */}
              {!calculationResult && (
                <div className="alert alert-info text-center">
                  <i className="fas fa-info-circle me-2"></i>
                  {tr("payroll:adjust_the_sample_input_data_above")}{" "}
                  <strong>{tr("payroll:calculate_payroll")}</strong> {" "}{tr("payroll:to_see_the_preview")}
                </div>
              )}
            </div>

            <div className="modal-footer bg-light">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <i className="fas fa-times me-2"></i>
                {tr("common:actions.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-md"
                onClick={handleConfirm}
                disabled={!hasCalculated}
              >
                <i className="fas fa-check me-2"></i>
                {tr("payroll:i_m_sure_the_calculation_is")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PayrollPreviewModal;
